import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  TIKTOK_VIDEOS,
  YOUTUBE_EN_SHORTS,
  YOUTUBE_EN_VIDEOS,
  YOUTUBE_ES_SHORTS,
  YOUTUBE_ES_VIDEOS,
} from "../../content/learnVideos";
import "./learnVideos.css";

// «Más videos» de la pestaña Videos de Aprender: TikTok, YouTube en español y YouTube en inglés
// como sub-paneles con carruseles (verticales para TikTok y Shorts, horizontales para videos).
// Las miniaturas son ligeras y el reproductor (iframe) solo se crea cuando se toca un video.

const SOURCE_IDS = ["tiktok", "youtube-es", "youtube-en"];
const TIKTOK_PROFILE = "https://www.tiktok.com/@yojuegoxima";

// Shorts: miniatura vertical (oardefault); los que no la tienen (hq) usan hqdefault recortada por CSS.
const ytThumb = (video, vertical) => {
  if (!vertical) return `https://i.ytimg.com/vi/${video.id}/mqdefault.jpg`;
  return `https://i.ytimg.com/vi/${video.id}/${video.hq ? "hqdefault" : "oardefault"}.jpg`;
};

const toYoutubeItems = (list, vertical) =>
  list.map((video) => ({
    key: `yt-${video.id}`,
    platform: "youtube",
    id: video.id,
    vertical,
    title: video.title,
    duration: video.duration || "",
    thumb: ytThumb(video, vertical),
    pillarbox: Boolean(vertical && video.hq),
    fallbackThumb: `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`,
    url: vertical ? `https://www.youtube.com/shorts/${video.id}` : `https://www.youtube.com/watch?v=${video.id}`,
  }));

const applyFallbackThumb = (event, item) => {
  if (item.fallbackThumb && event.currentTarget.src !== item.fallbackThumb) event.currentTarget.src = item.fallbackThumb;
};

const playerSrc = (item) =>
  item.platform === "tiktok"
    ? `https://www.tiktok.com/player/v1/${item.id}?autoplay=1&rel=0&description=1&music_info=0`
    : `https://www.youtube-nocookie.com/embed/${item.id}?autoplay=1&rel=0&playsinline=1&modestbranding=1`;

function VideoRow({ title, items, onOpen, t }) {
  const trackRef = useRef(null);
  const scrollBy = (direction) => {
    const track = trackRef.current;
    if (track) track.scrollBy({ left: direction * track.clientWidth * 0.85, behavior: "smooth" });
  };
  if (!items.length) return null;
  const vertical = items[0].vertical;
  return (
    <div className={`xvid-row ${vertical ? "is-vertical" : "is-wide"}`}>
      <div className="xvid-row-head">
        <strong>{title}</strong>
        <span className="xvid-count">{items.length}</span>
        <span className="xvid-arrows">
          <button type="button" onClick={() => scrollBy(-1)} aria-label={t("Anteriores", "Previous")}>‹</button>
          <button type="button" onClick={() => scrollBy(1)} aria-label={t("Siguientes", "Next")}>›</button>
        </span>
      </div>
      <div className="xvid-track" ref={trackRef}>
        {items.map((item, index) => (
          <button type="button" key={item.key} className={`xvid-thumb ${item.pillarbox ? "is-pillarbox" : ""}`} onClick={() => onOpen(items, index)}>
            <img
              src={item.thumb}
              alt=""
              loading="lazy"
              decoding="async"
              onError={(event) => applyFallbackThumb(event, item)}
            />
            <span className="xvid-play" aria-hidden="true">▶</span>
            {item.duration ? <small className="xvid-duration">{item.duration}</small> : null}
            <span className="xvid-title">{item.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function VideoPlayer({ list, index, onClose, onMove, t }) {
  const item = list[index];
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") onMove(1);
      if (event.key === "ArrowLeft") onMove(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onMove]);
  if (!item) return null;
  const platformName = item.platform === "tiktok" ? "TikTok" : "YouTube";
  return (
    <div className="xvid-modal" role="dialog" aria-modal="true" aria-label={item.title} onClick={onClose}>
      <div className={`xvid-modal-box ${item.vertical ? "is-vertical" : "is-wide"}`} onClick={(event) => event.stopPropagation()}>
        <div className="xvid-modal-frame">
          <iframe
            key={item.key}
            title={item.title}
            src={playerSrc(item)}
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture; clipboard-write"
            allowFullScreen
          />
        </div>
        <div className="xvid-modal-bar">
          <button type="button" className="xle-btn is-sm" onClick={() => onMove(-1)} disabled={index === 0}>
            ‹ {t("Anterior", "Previous")}
          </button>
          <span className="xvid-modal-title">
            {item.title}
            <small>
              {index + 1} / {list.length}
            </small>
          </span>
          <button type="button" className="xle-btn is-sm" onClick={() => onMove(1)} disabled={index >= list.length - 1}>
            {t("Siguiente", "Next")} ›
          </button>
        </div>
        <div className="xvid-modal-links">
          <a href={item.url} target="_blank" rel="noreferrer" className="xle-link">
            {t(`Abrir en ${platformName}`, `Open on ${platformName}`)} ↗
          </a>
          <button type="button" className="xle-btn is-sm" onClick={onClose}>
            ✕ {t("Cerrar", "Close")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LearnVideos({ t }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = String(searchParams.get("canal") || "").toLowerCase();
  const source = SOURCE_IDS.includes(requested) ? requested : "tiktok";
  const [player, setPlayer] = useState(null);

  const pickTitle = useCallback((title) => (typeof title === "string" ? title : t(title.es, title.en)), [t]);

  const sources = useMemo(() => {
    const tiktokItems = TIKTOK_VIDEOS.map((video) => ({
      key: `tt-${video.id}`,
      platform: "tiktok",
      id: video.id,
      vertical: true,
      title: pickTitle(video.title),
      thumb: video.thumb,
      url: `${TIKTOK_PROFILE}/video/${video.id}`,
    }));
    return {
      tiktok: {
        icon: "♪",
        tone: "tiktok",
        label: "TikTok",
        channel: TIKTOK_PROFILE,
        rows: [{ title: t("Videos cortos de @yojuegoxima", "Short videos from @yojuegoxima"), items: tiktokItems }],
      },
      "youtube-es": {
        icon: "▶",
        tone: "red",
        label: t("YouTube en español", "YouTube in Spanish"),
        channel: "https://www.youtube.com/@YVMAxima",
        rows: [
          { title: "Shorts", items: toYoutubeItems(YOUTUBE_ES_SHORTS, true) },
          { title: t("Videos", "Videos"), items: toYoutubeItems(YOUTUBE_ES_VIDEOS, false) },
        ],
      },
      "youtube-en": {
        icon: "▶",
        tone: "red",
        label: t("YouTube en inglés", "YouTube in English"),
        channel: "https://www.youtube.com/@YosdelMuino",
        rows: [
          { title: t("Videos", "Videos"), items: toYoutubeItems(YOUTUBE_EN_VIDEOS, false) },
          { title: "Shorts", items: toYoutubeItems(YOUTUBE_EN_SHORTS, true) },
        ],
      },
    };
  }, [pickTitle, t]);

  const chooseSource = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("canal", id);
    setSearchParams(next, { replace: true });
  };

  const openVideo = useCallback((list, index) => setPlayer({ list, index }), []);
  const closeVideo = useCallback(() => setPlayer(null), []);
  const moveVideo = useCallback(
    (step) =>
      setPlayer((current) => {
        if (!current) return current;
        const index = Math.min(current.list.length - 1, Math.max(0, current.index + step));
        return index === current.index ? current : { ...current, index };
      }),
    []
  );

  const active = sources[source];
  const total = (id) => sources[id].rows.reduce((sum, row) => sum + row.items.length, 0);

  return (
    <section className="xle-card xvid" id="mas-videos">
      <div className="xvid-head">
        <strong>🎬 {t("Más videos de XIMA", "More XIMA videos")}</strong>
        <div className="xvid-sources" role="tablist">
          {SOURCE_IDS.map((id) => (
            <button
              type="button"
              key={id}
              role="tab"
              aria-selected={id === source}
              className={`xvid-source is-${sources[id].tone} ${id === source ? "is-active" : ""}`}
              onClick={() => chooseSource(id)}
            >
              <span className="xvid-source-icon" aria-hidden="true">{sources[id].icon}</span>
              {sources[id].label}
              <span className="xvid-count">{total(id)}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="xvid-panel" role="tabpanel">
        {active.rows.map((row) => (
          <VideoRow key={`${source}-${row.title}`} title={row.title} items={row.items} onOpen={openVideo} t={t} />
        ))}
        <a href={active.channel} target="_blank" rel="noreferrer" className={`xle-btn is-sm is-${active.tone} xvid-channel`}>
          {source === "tiktok" ? t("Ver todos en TikTok", "See all on TikTok") : t("Ir al canal", "Go to channel")} ↗
        </a>
      </div>
      {player ? (
        <VideoPlayer list={player.list} index={player.index} onClose={closeVideo} onMove={moveVideo} t={t} />
      ) : null}
    </section>
  );
}
